<template>
  <div
    ref="outerContainerRef"
    data-testid="toast"
    class="
      tw-flex
      tw-items-center
      tw-justify-between
      tw-gap-3
      tw-px-4
      tw-py-3
      tw-min-h-[60px]
      tw-font-sans
      tw-border
      tw-border-solid
      tw-rounded-lg
      tw-shadow-toast
      tw-text-sm
      tw-leading-4
      tw-text-sf-gray-light
    "
    :class="[containerClass, variantClass, classes ?? '']"
  >
    <div class="tw-flex tw-gap-3 tw-items-center">
      <i
        ref="iconContainerRef"
        class="
          tw-text-[13px] tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center
          fa
        "
        :class="icon"
        data-testid="toast-icon"
      />
      <div class="tw-flex tw-flex-col tw-gap-0.5">
        <div
          v-show="!!$slots.title"
          class="tw-text-sf-gray tw-font-medium"
          data-testid="toast-title"
        >
          <SfEllipsis :length="availableWidthForText" :tooltip="titleTooltip">
            <slot name="title" />
          </SfEllipsis>
        </div>
        <div
          v-show="!!$slots.message"
          class="
            [&_a]:tw-underline
            [&_a]:tw-text-sf-primary-dark
            [&_a:hover]:tw-text-sf-primary-darker
          "
        >
          <template v-if="noMessageEllipsis">
            <slot name="message" />
          </template>
          <SfEllipsis v-else :height="32" :tooltip="messageTooltip">
            <slot name="message" />
          </SfEllipsis>
        </div>
      </div>
    </div>
    <div ref="actionsContainerRef" class="tw-flex tw-items-center">
      <div class="tw-mr-1">
        <slot name="action" />
      </div>
      <SfButtonDs
        variant="tertiary"
        size="sm"
        icon-only
        icon-before="close"
        :aria-label="I18n.t('common.close')"
        data-testid="toast-dismiss"
        @click="handleDismiss"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import type { ColorVariantsType, SizesType } from '@app/shared/constants'
import { ColorVariants, Sizes } from '@app/shared/constants'
import { SfButtonDs } from '@silverfin/ui-components'
import SfEllipsis from '../sf-ellipsis/sf-ellipsis.vue'

const I18n = window.I18n

interface Props {
  autoDismissDelay?: number
  size?: Extract<SizesType, 'sm' | 'md' | 'lg'>
  titleTooltip?: string
  messageTooltip?: string
  variant?: ColorVariantsType
  classes?: string
  // When true the message slot is rendered as-is, without the SfEllipsis
  // wrapper — use this when the message contains its own layout (e.g. a
  // link on its own line) that shouldn't be truncated or get a hover
  // tooltip.
  noMessageEllipsis?: boolean
}

const {
  autoDismissDelay,
  size = Sizes.MD,
  variant = ColorVariants.DEFAULT,
  classes = '',
  noMessageEllipsis = false,
} = defineProps<Props>()

const emits =
  defineEmits<{
    dismiss: []
  }>()

const outerContainerRef = ref<HTMLElement | null>(null)
const iconContainerRef = ref<HTMLElement | null>(null)
const actionsContainerRef = ref<HTMLElement | null>(null)

let autoDismissTimeout: number | null = null

onMounted(() => {
  if (autoDismissDelay) {
    autoDismissTimeout = window.setTimeout(() => {
      emits('dismiss')
    }, autoDismissDelay)
  }
})

onBeforeUnmount(() => {
  if (autoDismissTimeout) {
    window.clearTimeout(autoDismissTimeout)
  }
})

const containerClass = computed(() => {
  return {
    'tw-w-[240px]': size === Sizes.SM,
    'tw-w-[360px]': size === Sizes.MD,
    'tw-w-[600px]': size === Sizes.LG,
  }
})

const variantClass = computed(() => {
  return {
    'tw-bg-white tw-border-sf-snow-darker': variant === ColorVariants.DEFAULT,
    'tw-bg-sf-info-subtle-1 tw-border-sf-info-light-1':
      variant === ColorVariants.INFO,
    'tw-bg-emerald-50 tw-border-sf-success-lighter-2':
      variant === ColorVariants.SUCCESS,
    'tw-bg-yellow-50 tw-border-sf-warning-lighter-2':
      variant === ColorVariants.WARNING,
    'tw-bg-red-50 tw-border-sf-danger-lighter-2':
      variant === ColorVariants.DANGER,
    'tw-bg-violet-50 tw-border-sf-ai-lighter-1': variant === ColorVariants.AI,
  }
})

const icon = computed(() => {
  return {
    'fa-info-circle tw-text-sf-gray-light': variant === ColorVariants.DEFAULT,
    'fa-check-circle tw-text-sf-success-light-1':
      variant === ColorVariants.SUCCESS,
    'fa-info-circle tw-text-sf-warning-light-1':
      variant === ColorVariants.WARNING,
    'fa-exclamation-triangle tw-text-sf-danger-light-1':
      variant === ColorVariants.DANGER,
    'fa-info-circle tw-text-sf-info-light-2': variant === ColorVariants.INFO,
    'fa-wand-magic-sparkles tw-text-sf-ai-light-2':
      variant === ColorVariants.AI,
  }
})

const availableWidthForText = computed(() => {
  if (
    !outerContainerRef.value ||
    !iconContainerRef.value ||
    !actionsContainerRef.value
  ) {
    return 300
  }

  return (
    outerContainerRef.value.offsetWidth -
    iconContainerRef.value.offsetWidth -
    actionsContainerRef.value.offsetWidth -
    32 -
    12 -
    12 -
    2
  )
})

function handleDismiss(): void {
  emits('dismiss')
}
</script>
